import React, { useEffect } from 'react';
import { Col, Divider, Row } from 'antd';
import { isEqual } from 'lodash';

import {
  CustomerDocumentTypeEnum,
  CustomerHistoryDetailsModel,
  FullCustomerInfoResponseModel,
  LocationModel,
} from 'api/adminApiClient';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';
import BaseAlert from 'components/wrappers/BaseAlert';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import { getCustomerHistoryFile } from 'redux/reducers/clientSlice';
import {
  getAllCountries,
  getAllStates,
} from 'redux/reducers/regionSlice';
import {
  countriesSelector,
  statesSelector,
} from 'redux/selectors/regionSelectors';
import { downloadFile, getFileNameWithoutExt } from 'utils/fileUtils';
import HistoryLog from 'components/ActivitiesDetailsSection/HistorySection/HistoryList/HistoryCard/HistoryLog';
import HistoryField from 'components/ActivitiesDetailsSection/HistorySection/HistoryList/HistoryCard/HistoryField';
import { getYesNoAnswer } from '../../../../../utils';
import {
  getContractRenewalTermLabel,
  getContractedMBPServiceLinesLabel,
  getCustomerAgreementTypeLabel,
  getIndemnificationTypeLabel,
} from 'utils/clientUtils';

import styles from 'components/ActivitiesDetailsSection/HistorySection/HistoryList/HistoryCard/HistoryCard.module.less';

type Props = {
  customerId: string;
  historyDetails: CustomerHistoryDetailsModel;
  historyId: string;
};

const contractReqPath = (
  d: FullCustomerInfoResponseModel | undefined,
) => d?.additionalInfo?.contractInformation?.contractRequirements;

const noticePartyPath = (
  d: FullCustomerInfoResponseModel | undefined,
) => d?.additionalInfo?.contractInformation?.noticeParty;

export default function GeneralContractInfoDetailsCard(props: Props) {
  const { historyDetails, customerId, historyId } = props;
  const { oldData, newData } = historyDetails;
  const dispatch = useAppDispatch();
  const countries = useAppSelector(countriesSelector);
  const states = useAppSelector(statesSelector);

  useEffect(() => {
    dispatch(getAllCountries());
    dispatch(getAllStates());
  }, [dispatch]);

  /** History snapshot from BE is asymmetric: OldData has resolved
   * `state`/`country`/`countryCode` strings (via LocationMappers),
   * NewData carries only `stateId`/`countryId`. We backfill the
   * display strings on FE from the same Redux caches the form uses,
   * so comparisons and rendering work off uniform values on both sides. */
  const resolveLocation = (
    loc?: LocationModel,
  ): LocationModel | undefined => {
    if (!loc) return loc;
    const resolvedCountry =
      loc.country ?? countries.find((c) => c.id === loc.countryId)?.name;
    const resolvedState =
      loc.state ?? states.find((s) => s.id === loc.stateId)?.name;
    return {
      ...loc,
      country: resolvedCountry,
      state: resolvedState,
    };
  };

  const oIns = oldData?.additionalInfo?.contractInformation?.insuranceRequirements;
  const nIns = newData?.additionalInfo?.contractInformation?.insuranceRequirements;
  const oCr = contractReqPath(oldData);
  const nCr = contractReqPath(newData);
  const oNp = noticePartyPath(oldData);
  const nNp = noticePartyPath(newData);
  const oNoticeLocation = resolveLocation(oNp?.location);
  const nNoticeLocation = resolveLocation(nNp?.location);

  const hasInsuranceRequirementsChanged = !isEqual(oIns, nIns);
  const hasRequiresCustomCOIChanged = !isEqual(
    oIns?.requiresCustomCOI,
    nIns?.requiresCustomCOI,
  );
  const hasSubmittedToChanged = !isEqual(oIns?.submittedTo, nIns?.submittedTo);
  const hasCertificateOfInsuranceFileChanged = !isEqual(
    oIns?.certificateOfInsuranceCreatedFileKey,
    nIns?.certificateOfInsuranceCreatedFileKey,
  );
  const hasContractSpecialInstructionsChanged = !isEqual(
    oCr?.contractSpecialInstructions,
    nCr?.contractSpecialInstructions,
  );

  const hasSowRequiredChanged = !isEqual(oCr?.sowRequired, nCr?.sowRequired);
  const hasSowSpecialInstructionsChanged = !isEqual(
    oCr?.sowSpecialInstructions,
    nCr?.sowSpecialInstructions,
  );
  const hasSowTemplateFileKeyChanged = !isEqual(
    oCr?.sowTemplateFileKey,
    nCr?.sowTemplateFileKey,
  );

  const hasCanContractBeAssignedChanged = !isEqual(
    oCr?.canContractBeAssigned,
    nCr?.canContractBeAssigned,
  );
  const hasUseOfNameLogoPermittedChanged = !isEqual(
    oCr?.useOfNameLogoPermitted,
    nCr?.useOfNameLogoPermitted,
  );
  const hasCustomerAgreementTypeChanged = !isEqual(
    oCr?.customerAgreementType,
    nCr?.customerAgreementType,
  );
  const hasGoverningLawChanged = !isEqual(
    oCr?.governingLawJurisdiction,
    nCr?.governingLawJurisdiction,
  );
  const hasIndemnificationTypeChanged = !isEqual(
    oCr?.indemnificationType,
    nCr?.indemnificationType,
  );
  const hasLimitationOfLiabilityChanged = !isEqual(
    oCr?.limitationOfLiability,
    nCr?.limitationOfLiability,
  );
  const hasRightToInspectChanged = !isEqual(
    oCr?.rightToInspectOrAudit,
    nCr?.rightToInspectOrAudit,
  );
  const hasContractedLinesChanged = !isEqual(
    oCr?.contractedMBPServiceLines,
    nCr?.contractedMBPServiceLines,
  );
  const hasDataPrivacyReqChanged = !isEqual(
    oCr?.dataPrivacyRequirements,
    nCr?.dataPrivacyRequirements,
  );
  const hasDataPrivacyInstrChanged = !isEqual(
    oCr?.dataPrivacyInstructions,
    nCr?.dataPrivacyInstructions,
  );
  const hasRenewalTermChanged = !isEqual(
    oCr?.contractRenewalTerm,
    nCr?.contractRenewalTerm,
  );
  const hasEffectiveDateChanged = !isEqual(
    oCr?.contractEffectiveDate,
    nCr?.contractEffectiveDate,
  );
  const hasExpirationDateChanged = !isEqual(
    oCr?.contractExpirationDate,
    nCr?.contractExpirationDate,
  );
  const hasNoticePeriodChanged = !isEqual(
    oCr?.noticePeriodInDays,
    nCr?.noticePeriodInDays,
  );
  const hasDeclinationReasonChanged = !isEqual(
    oCr?.declinationReason,
    nCr?.declinationReason,
  );

  const hasAttentionPartyChanged = !isEqual(
    oNp?.attentionParty,
    nNp?.attentionParty,
  );
  const hasNoticeStreetChanged = !isEqual(
    oNoticeLocation?.address,
    nNoticeLocation?.address,
  );
  const hasNoticeCityChanged = !isEqual(
    oNoticeLocation?.city || oNoticeLocation?.internationalCityName,
    nNoticeLocation?.city || nNoticeLocation?.internationalCityName,
  );
  const hasNoticeStateChanged = !isEqual(
    oNoticeLocation?.state || oNoticeLocation?.internationalStateName,
    nNoticeLocation?.state || nNoticeLocation?.internationalStateName,
  );
  const hasNoticeCountryChanged = !isEqual(
    oNoticeLocation?.country,
    nNoticeLocation?.country,
  );
  const hasNoticeZipChanged = !isEqual(
    oNoticeLocation?.zipCode,
    nNoticeLocation?.zipCode,
  );
  const hasNoticeEmailChanged = !isEqual(oNp?.email, nNp?.email);
  const hasNoticePhoneChanged = !isEqual(oNp?.phone, nNp?.phone);
  const hasNoticeFaxChanged = !isEqual(oNp?.faxNumber, nNp?.faxNumber);
  const hasNoticeMethodChanged = !isEqual(oNp?.noticeMethod, nNp?.noticeMethod);

  const hasNoticeLocationChanged =
    hasNoticeStreetChanged ||
    hasNoticeCityChanged ||
    hasNoticeStateChanged ||
    hasNoticeCountryChanged ||
    hasNoticeZipChanged;
  const hasNoticePartyChanged =
    hasAttentionPartyChanged ||
    hasNoticeLocationChanged ||
    hasNoticeEmailChanged ||
    hasNoticePhoneChanged ||
    hasNoticeFaxChanged ||
    hasNoticeMethodChanged;

  const handleCertificateOfInsuranceFileDownload = async (fileKey?: string) => {
    const result = await dispatch(
      getCustomerHistoryFile({
        customerId,
        fileKey,
        documentType: CustomerDocumentTypeEnum.CertificateOfInsurance,
      }),
    );

    if (getCustomerHistoryFile.fulfilled.match(result)) {
      try {
        await downloadFile({
          downloadUrl: result.payload.downloadUrl,
          fileName: result.payload.fileName,
        });
      } catch (e: any) {
        BaseAlert.error(e.message);
      }
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const handleFileDownload = async (
    fileKey: string,
    documentType: CustomerDocumentTypeEnum,
  ) => {
    const result = await dispatch(
      getCustomerHistoryFile({
        customerId,
        fileKey,
        documentType,
      }),
    );

    if (getCustomerHistoryFile.fulfilled.match(result)) {
      try {
        await downloadFile({
          downloadUrl: result.payload.downloadUrl,
          fileName: result.payload.fileName,
        });
      } catch (e: any) {
        BaseAlert.error(e.message);
      }
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const renderInsuranceRequirementsSection = (
    customerDetails?: FullCustomerInfoResponseModel,
  ) => {
    const sectionName = 'insurance-requirements';

    return (
      <>
        <Row gutter={[10, 10]}>
          <Col xs={24}>
            <div className={styles.cardTitle}>Insurance requirements</div>
          </Col>

          {hasRequiresCustomCOIChanged && (
            <HistoryField
              title="Requires custom COI?"
              value={getYesNoAnswer(
                customerDetails?.additionalInfo?.contractInformation
                  ?.insuranceRequirements?.requiresCustomCOI,
              )}
              dataId={`${sectionName}-requires-custom-coi-field`}
            />
          )}

          {hasSubmittedToChanged && (
            <HistoryField
              title="Submitted to"
              value={
                customerDetails?.additionalInfo?.contractInformation
                  ?.insuranceRequirements?.submittedTo
              }
              dataId={`${sectionName}-submitted-to-field`}
            />
          )}

          {hasCertificateOfInsuranceFileChanged && (
            <HistoryField
              title="Certificate of insurance created"
              emptyText="No file"
              value={
                customerDetails?.additionalInfo?.contractInformation
                  ?.insuranceRequirements
                  ?.certificateOfInsuranceCreatedFileKey &&
                customerDetails?.additionalInfo?.contractInformation
                  ?.insuranceRequirements
                  ?.certificateOfInsuranceCreatedFileName && (
                  <span
                    className="link-imitation"
                    onClick={() =>
                      handleCertificateOfInsuranceFileDownload(
                        customerDetails?.additionalInfo
                          ?.contractInformation?.insuranceRequirements
                          ?.certificateOfInsuranceCreatedFileKey,
                      )
                    }
                  >
                    {getFileNameWithoutExt(
                      customerDetails?.additionalInfo
                        ?.contractInformation?.insuranceRequirements
                        ?.certificateOfInsuranceCreatedFileName,
                    )}
                  </span>
                )
              }
              dataId={`${sectionName}-certificate-insurance-created-field`}
            />
          )}
        </Row>

        <Divider />
      </>
    );
  };

  const renderContractRequirementsSection = (
    customerDetails?: FullCustomerInfoResponseModel,
  ) => {
    const sectionName = 'contract-requirements';
    const cr = contractReqPath(customerDetails);

    return (
      <>
        <Row gutter={[10, 10]}>
          <Col xs={24}>
            <div className={styles.cardTitle}>Contract requirements</div>
          </Col>

          {hasCanContractBeAssignedChanged && (
            <HistoryField
              title="Can the contract be assigned?"
              value={getYesNoAnswer(cr?.canContractBeAssigned)}
              dataId={`${sectionName}-can-assign-field`}
            />
          )}
          {hasUseOfNameLogoPermittedChanged && (
            <HistoryField
              title="Use of name / logo permitted?"
              value={getYesNoAnswer(cr?.useOfNameLogoPermitted)}
              dataId={`${sectionName}-name-logo-field`}
            />
          )}
          {hasCustomerAgreementTypeChanged && (
            <HistoryField
              title="Customer agreement type"
              value={getCustomerAgreementTypeLabel(cr?.customerAgreementType)}
              dataId={`${sectionName}-agreement-type-field`}
            />
          )}
          {hasGoverningLawChanged && (
            <HistoryField
              title="Governing law jurisdiction"
              value={cr?.governingLawJurisdiction}
              dataId={`${sectionName}-governing-law-field`}
            />
          )}
          {hasIndemnificationTypeChanged && (
            <HistoryField
              title="Indemnification type"
              value={getIndemnificationTypeLabel(cr?.indemnificationType)}
              dataId={`${sectionName}-indemnification-field`}
            />
          )}
          {hasLimitationOfLiabilityChanged && (
            <HistoryField
              title="Limitation of liability"
              value={cr?.limitationOfLiability}
              dataId={`${sectionName}-limitation-field`}
            />
          )}
          {hasRightToInspectChanged && (
            <HistoryField
              title="Right to inspect or audit?"
              value={getYesNoAnswer(cr?.rightToInspectOrAudit)}
              dataId={`${sectionName}-inspect-field`}
            />
          )}
          {hasContractedLinesChanged && (
            <HistoryField
              title="Contracted MBP service line(s)"
              value={
                cr?.contractedMBPServiceLines
                  ?.map((line) => getContractedMBPServiceLinesLabel(line))
                  .filter(Boolean)
                  .join(', ') || undefined
              }
              dataId={`${sectionName}-service-lines-field`}
            />
          )}
          {hasDataPrivacyReqChanged && (
            <HistoryField
              title="Data privacy requirements"
              value={getYesNoAnswer(cr?.dataPrivacyRequirements)}
              dataId={`${sectionName}-data-privacy-req-field`}
            />
          )}
          {hasDataPrivacyInstrChanged && (
            <HistoryField
              title="Data privacy instructions"
              value={cr?.dataPrivacyInstructions}
              dataId={`${sectionName}-data-privacy-instr-field`}
            />
          )}
          {hasRenewalTermChanged && (
            <HistoryField
              title="Contract renewal term"
              value={getContractRenewalTermLabel(cr?.contractRenewalTerm)}
              dataId={`${sectionName}-renewal-term-field`}
            />
          )}
          {hasEffectiveDateChanged && (
            <HistoryField
              title="Contract effective date"
              value={
                cr?.contractEffectiveDate
                  ? new Date(
                      cr.contractEffectiveDate as Date,
                    ).toLocaleDateString()
                  : undefined
              }
              dataId={`${sectionName}-effective-date-field`}
            />
          )}
          {hasExpirationDateChanged && (
            <HistoryField
              title="Contract expiration date"
              value={
                cr?.contractExpirationDate
                  ? new Date(
                      cr.contractExpirationDate as Date,
                    ).toLocaleDateString()
                  : undefined
              }
              dataId={`${sectionName}-expiration-date-field`}
            />
          )}
          {hasNoticePeriodChanged && (
            <HistoryField
              title="Notice period (in days)"
              value={cr?.noticePeriodInDays}
              dataId={`${sectionName}-notice-period-field`}
            />
          )}
          {hasSowRequiredChanged && (
            <HistoryField
              title="SOW required"
              value={getYesNoAnswer(cr?.sowRequired)}
              dataId={`${sectionName}-SOW-required-field`}
            />
          )}
          {hasSowSpecialInstructionsChanged && (
            <HistoryField
              title="SOW special instructions"
              value={cr?.sowSpecialInstructions}
              dataId={`${sectionName}-SOW-special-instructions-field`}
            />
          )}
          {hasSowTemplateFileKeyChanged && (
            <HistoryField
              title="SOW template"
              emptyText="No file"
              value={
                cr?.sowTemplateFileKey && cr?.sowTemplateFileName ? (
                  <span
                    className="link-imitation"
                    onClick={() =>
                      handleFileDownload(
                        cr.sowTemplateFileKey as string,
                        CustomerDocumentTypeEnum.SowTemplate,
                      )
                    }
                  >
                    {getFileNameWithoutExt(cr.sowTemplateFileName)}
                  </span>
                ) : undefined
              }
              dataId={`${sectionName}-SOW-template-field`}
            />
          )}
          {hasDeclinationReasonChanged && (
            <HistoryField
              title="Declination reason"
              value={cr?.declinationReason}
              dataId={`${sectionName}-declination-reason-field`}
            />
          )}
          {hasContractSpecialInstructionsChanged && (
            <HistoryField
              title="Contract special instructions"
              value={cr?.contractSpecialInstructions}
              dataId={`${sectionName}-contract-special-instructions-field`}
            />
          )}
        </Row>

        <Divider />
      </>
    );
  };

  const renderNoticePartySection = (
    customerDetails?: FullCustomerInfoResponseModel,
  ) => {
    const sectionName = 'notice-party';
    const np = noticePartyPath(customerDetails);
    const loc = resolveLocation(np?.location);

    return (
      <>
        <Row gutter={[10, 10]}>
          <Col xs={24}>
            <div className={styles.cardTitle}>Notice party</div>
          </Col>

          {hasAttentionPartyChanged && (
            <HistoryField
              title="Attention party"
              value={np?.attentionParty}
              dataId={`${sectionName}-attention-field`}
            />
          )}
          {hasNoticeLocationChanged && (
            <>
              <HistoryField
                title="Street"
                value={loc?.address}
                dataId={`${sectionName}-street-field`}
              />
              <HistoryField
                title="City"
                value={loc?.city || loc?.internationalCityName}
                dataId={`${sectionName}-city-field`}
              />
              <HistoryField
                title="State / province"
                value={loc?.state || loc?.internationalStateName}
                dataId={`${sectionName}-state-field`}
              />
              <HistoryField
                title="Country"
                value={loc?.country}
                dataId={`${sectionName}-country-field`}
              />
              <HistoryField
                title="ZIP code"
                value={loc?.zipCode}
                dataId={`${sectionName}-zip-field`}
              />
            </>
          )}
          {hasNoticeEmailChanged && (
            <HistoryField
              title="Email"
              value={np?.email}
              dataId={`${sectionName}-email-field`}
            />
          )}
          {hasNoticePhoneChanged && (
            <HistoryField
              title="Phone"
              value={np?.phone}
              dataId={`${sectionName}-phone-field`}
            />
          )}
          {hasNoticeFaxChanged && (
            <HistoryField
              title="Fax number"
              value={np?.faxNumber}
              dataId={`${sectionName}-fax-field`}
            />
          )}
          {hasNoticeMethodChanged && (
            <HistoryField
              title="Notice method"
              value={np?.noticeMethod}
              dataId={`${sectionName}-method-field`}
            />
          )}
        </Row>

        <Divider />
      </>
    );
  };

  const showInsurance =
    hasRequiresCustomCOIChanged ||
    hasSubmittedToChanged ||
    hasCertificateOfInsuranceFileChanged;

  const showContractRequirements =
    hasCanContractBeAssignedChanged ||
    hasUseOfNameLogoPermittedChanged ||
    hasCustomerAgreementTypeChanged ||
    hasGoverningLawChanged ||
    hasIndemnificationTypeChanged ||
    hasLimitationOfLiabilityChanged ||
    hasRightToInspectChanged ||
    hasContractedLinesChanged ||
    hasDataPrivacyReqChanged ||
    hasDataPrivacyInstrChanged ||
    hasRenewalTermChanged ||
    hasEffectiveDateChanged ||
    hasExpirationDateChanged ||
    hasNoticePeriodChanged ||
    hasSowRequiredChanged ||
    hasSowSpecialInstructionsChanged ||
    hasSowTemplateFileKeyChanged ||
    hasDeclinationReasonChanged ||
    hasContractSpecialInstructionsChanged;

  const hasAnyDisplayableChange =
    (hasInsuranceRequirementsChanged && showInsurance) ||
    showContractRequirements ||
    hasNoticePartyChanged;

  const renderAdditionalInformation = (
    customerDetails?: FullCustomerInfoResponseModel,
  ) => {
    return (
      <>
        {hasInsuranceRequirementsChanged && showInsurance &&
          renderInsuranceRequirementsSection(customerDetails)}
        {showContractRequirements &&
          renderContractRequirementsSection(customerDetails)}
        {hasNoticePartyChanged && renderNoticePartySection(customerDetails)}
      </>
    );
  };

  if (!hasAnyDisplayableChange) {
    return null;
  }

  return (
    <HistoryLog
      historyId={historyId}
      oldData={renderAdditionalInformation(oldData)}
      newData={renderAdditionalInformation(newData)}
    />
  );
}
